/**
 * EdgeDocs Caret - Basic visual caret for Phase 1A
 *
 * Simple blinking vertical line that shows where the user is typing.
 * Integrates with canvas-based text rendering and cursor position tracking.
 */

import { Point } from './EdgeDocsTypes';
import { InputLogger } from '../../../../utils/loggers/InputLogger';

export interface CaretConfig {
  color?: string;
  width?: number;
  blinkInterval?: number;
  height?: number;

  // 🆕 2026-01-20: Animation options (MS Word-style smooth caret movement)
  animation?: {
    enabled: boolean;
    durationMs?: number;         // Animation duration (default: 80ms)
    easing?: 'linear' | 'ease-out' | 'ease-in-out';  // Easing function (default: 'ease-out')
  };

  // 🆕 V8.8: Diagnostic options for debugging and testing
  debug?: {
    enabled: boolean;
    showBounds?: boolean;        // Draw caret bounding box
    showCrosshair?: boolean;     // Draw crosshair at position
    showCoordinates?: boolean;   // Display (x, y) near caret
    logUpdates?: boolean;        // Console log every position change
    showSource?: boolean;        // Show if position from caret_map or fallback
    highlightColor?: string;     // Color for debug overlays
  };

  // 🆕 V8.8: Validation options for robustness
  validation?: {
    enabled: boolean;
    canvasBounds?: { width: number; height: number };
    warnOnInvalid?: boolean;
    clampToCanvas?: boolean;
    throwOnNaN?: boolean;
  };
}

export interface CaretPosition {
  x: number;
  y: number;
  height: number;
}

export class EdgeDocsCaret {
  private ctx: CanvasRenderingContext2D;
  private position: CaretPosition;           // Current rendered position (may be mid-animation)
  private targetPosition: CaretPosition;     // Target position for animation
  private visible = true;
  private blinkTimer: number | null = null;
  private isActive = false;

  // world->device params
  private scale = 1;
  private dpr = Math.max(1, window.devicePixelRatio || 1);

  // 🆕 2026-01-20: Animation state for smooth caret movement (MS Word-style)
  private animationStartTime: number | null = null;
  private animationStartX: number = 0;
  private animationFrameId: number | null = null;
  private isFirstPosition: boolean = true;    // Skip animation for initial position

  // 🆕 V8.8: Debug tracking
  private debugInfo?: {
    updateCount: number;
    lastUpdate: number;
    positionSource: 'caret_map' | 'fallback' | 'unknown';
    graphemeIndex?: number;
  };

  private config: {
    color: string;
    width: number;
    blinkInterval: number;
    height: number;
    animation?: CaretConfig['animation'];
    debug?: CaretConfig['debug'];
    validation?: CaretConfig['validation'];
  };

  constructor(private canvas: HTMLCanvasElement, config: CaretConfig = {}) {
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas context not available for caret');
    this.ctx = ctx;

    this.config = {
      color: config.color ?? '#000000',
      width: config.width ?? 1,
      blinkInterval: config.blinkInterval ?? 500,
      height: config.height ?? 20,
      animation: config.animation ?? { enabled: true, durationMs: 80, easing: 'ease-out' },
      debug: config.debug,
      validation: config.validation
    };

    this.position = { x: 0, y: 0, height: this.config.height };
    this.targetPosition = { x: 0, y: 0, height: this.config.height };
  }

  /** Call whenever scale/DPR changes (same values used for base canvas). */
  configureTransform(scale: number, dpr: number) {
    this.scale = scale;
    this.dpr = dpr;
  }

  /** Set overlay canvas backing-store & CSS sizes */
  resizeOverlayCanvas(worldW: number, worldH: number) {
    const backingW = Math.round(worldW * this.scale * this.dpr);
    const backingH = Math.round(worldH * this.scale * this.dpr);
    if (this.canvas.width !== backingW) this.canvas.width = backingW;
    if (this.canvas.height !== backingH) this.canvas.height = backingH;
    this.canvas.style.width = `${worldW * this.scale}px`;
    this.canvas.style.height = `${worldH * this.scale}px`;
  }

  setPosition(pos: CaretPosition): void {
    // 🆕 V8.8: Validate position if enabled
    let validatedPos = pos;
    if (this.config.validation?.enabled) {
      const validation = this.validatePosition(pos);
      if (!validation.valid && validation.corrected) {
        validatedPos = validation.corrected;
      }
    }

    // 🆕 2026-01-20: MS Word-style smooth caret animation
    // Only animate horizontal movement; Y changes are instant (line changes)
    const shouldAnimate = this.config.animation?.enabled &&
                          !this.isFirstPosition &&
                          Math.abs(validatedPos.y - this.position.y) < 1; // Same line (Y unchanged)

    if (shouldAnimate) {
      // Cancel any existing animation
      if (this.animationFrameId !== null) {
        cancelAnimationFrame(this.animationFrameId);
        this.animationFrameId = null;
      }

      // Store animation start state
      this.animationStartX = this.position.x;
      this.animationStartTime = performance.now();
      this.targetPosition = { ...validatedPos };

      // Update Y and height immediately (only X animates)
      this.position.y = validatedPos.y;
      this.position.height = validatedPos.height;

      // Start animation loop
      this.animateToTarget();
    } else {
      // No animation - instant position update
      this.position = { ...validatedPos };
      this.targetPosition = { ...validatedPos };
      this.isFirstPosition = false;
      this.redraw();
    }

    this.visible = true;
    this.restartBlinking();
  }

  /**
   * 🆕 2026-01-20: Animate caret X position to target using easing
   * MS Word-style smooth horizontal movement with ease-out timing
   */
  private animateToTarget(): void {
    if (this.animationStartTime === null) return;

    const elapsed = performance.now() - this.animationStartTime;
    const duration = this.config.animation?.durationMs ?? 80;
    const progress = Math.min(1, elapsed / duration);

    // Apply easing function
    const easedProgress = this.applyEasing(progress);

    // Interpolate X position
    this.position.x = this.animationStartX +
      (this.targetPosition.x - this.animationStartX) * easedProgress;

    this.redraw();

    if (progress < 1) {
      // Continue animation
      this.animationFrameId = requestAnimationFrame(() => this.animateToTarget());
    } else {
      // Animation complete - snap to exact target
      this.position.x = this.targetPosition.x;
      this.animationStartTime = null;
      this.animationFrameId = null;
      this.redraw();
    }
  }

  /**
   * 🆕 2026-01-20: Apply easing function to animation progress
   */
  private applyEasing(t: number): number {
    const easing = this.config.animation?.easing ?? 'ease-out';

    switch (easing) {
      case 'linear':
        return t;
      case 'ease-out':
        // Cubic ease-out: decelerates as it approaches target (natural "settling" feel)
        return 1 - Math.pow(1 - t, 3);
      case 'ease-in-out':
        // Smooth acceleration then deceleration
        return t < 0.5
          ? 4 * t * t * t
          : 1 - Math.pow(-2 * t + 2, 3) / 2;
      default:
        return t;
    }
  }
  
  /**
   * 🆕 V8.8: Set position with debug metadata
   * Use this for enhanced debugging when position comes from ProductionCaretSystem
   */
  setPositionWithMetadata(
    pos: CaretPosition,
    metadata?: { source: 'caret_map' | 'fallback'; graphemeIndex?: number }
  ): void {
    // Update debug info first (before setPosition which may animate)
    if (this.config.debug?.enabled) {
      this.debugInfo = {
        updateCount: (this.debugInfo?.updateCount || 0) + 1,
        lastUpdate: Date.now(),
        positionSource: metadata?.source || 'unknown',
        graphemeIndex: metadata?.graphemeIndex
      };

      if (this.config.debug.logUpdates) {
        console.log(`[CaretDebug] Position update #${this.debugInfo.updateCount}:`, {
          x: pos.x.toFixed(2),
          y: pos.y.toFixed(2),
          height: pos.height.toFixed(2),
          source: metadata?.source,
          graphemeIndex: metadata?.graphemeIndex
        });
      }
    }

    // Use the main setPosition which handles animation
    this.setPosition(pos);
  }

  /**
   * Set caret position using Point + height
   */
  setPositionFromPoint(point: Point, height: number): void {
    this.setPosition({
      x: point.x,
      y: point.y,
      height
    });
  }

  setHeight(height: number): void {
    this.position.height = height;
    this.redraw();
  }

  show(): void {
    // InputLogger.caretDiag.show(this.isActive); // Disabled: consolidated in ClickEventCollector
    if (this.isActive) return;
    this.isActive = true;
    this.visible = true;
    this.startBlinking();
    this.redraw();
  }

  hide(): void {
    // InputLogger.caretDiag.hide(this.isActive); // Disabled: consolidated in ClickEventCollector
    if (!this.isActive) return;
    this.isActive = false;
    this.visible = false;
    this.stopBlinking();
    this.redraw();
  }

  /**
   * Toggle caret visibility (useful for focus/blur)
   */
  toggle(): void {
    if (this.isActive) {
      this.hide();
    } else {
      this.show();
    }
  }

  /** Clears overlay (device space) and draws caret (world space) if visible. */
  redraw(): void {
    // 1) clear overlay in device coords
    this.ctx.save();
    this.ctx.setTransform(1, 0, 0, 1, 0, 0);
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    this.ctx.restore();

    if (!this.isActive) return;

    // 2) draw caret in world coords (apply world->device transform)
    this.ctx.save();
    this.ctx.setTransform(this.scale * this.dpr, 0, 0, this.scale * this.dpr, 0, 0);

    // Draw main caret (respects blink state)
    if (this.visible) {
      this.ctx.strokeStyle = this.config.color;
      this.ctx.lineWidth = this.config.width;
      this.ctx.beginPath();
      this.ctx.moveTo(this.position.x, this.position.y);
      this.ctx.lineTo(this.position.x, this.position.y + this.position.height);
      this.ctx.stroke();
    }

    // 🆕 V8.8: Draw diagnostic overlays (always visible, not affected by blink)
    if (this.config.debug?.enabled) {
      this.drawDebugOverlays();
    }

    this.ctx.restore();
  }
  
  /**
   * 🆕 V8.8: Draw diagnostic overlays for debugging
   * Called from redraw() when debug mode is enabled
   */
  private drawDebugOverlays(): void {
    const debugColor = this.config.debug?.highlightColor || '#ff00ff';
    
    // Bounding box
    if (this.config.debug?.showBounds) {
      this.ctx.strokeStyle = debugColor;
      this.ctx.lineWidth = 1;
      this.ctx.setLineDash([2, 2]);
      this.ctx.strokeRect(
        this.position.x - 2,
        this.position.y,
        4,
        this.position.height
      );
      this.ctx.setLineDash([]);
    }
    
    // Crosshair
    if (this.config.debug?.showCrosshair) {
      this.ctx.strokeStyle = debugColor;
      this.ctx.lineWidth = 0.5;
      this.ctx.globalAlpha = 0.6;
      this.ctx.setLineDash([3, 3]);
      
      // Horizontal line at top
      this.ctx.beginPath();
      this.ctx.moveTo(this.position.x - 15, this.position.y);
      this.ctx.lineTo(this.position.x + 15, this.position.y);
      this.ctx.stroke();
      
      // Horizontal line at bottom
      this.ctx.beginPath();
      this.ctx.moveTo(this.position.x - 15, this.position.y + this.position.height);
      this.ctx.lineTo(this.position.x + 15, this.position.y + this.position.height);
      this.ctx.stroke();
      
      // Vertical extension
      this.ctx.beginPath();
      this.ctx.moveTo(this.position.x, this.position.y - 5);
      this.ctx.lineTo(this.position.x, this.position.y + this.position.height + 5);
      this.ctx.stroke();
      
      this.ctx.setLineDash([]);
      this.ctx.globalAlpha = 1.0;
    }
    
    // Coordinates text
    if (this.config.debug?.showCoordinates && this.debugInfo) {
      this.ctx.font = '10px monospace';
      this.ctx.fillStyle = debugColor;
      this.ctx.globalAlpha = 0.9;
      
      // Background for text readability
      const coordText = `(${this.position.x.toFixed(1)}, ${this.position.y.toFixed(1)})`;
      const textMetrics = this.ctx.measureText(coordText);
      const textHeight = 12;
      
      this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      this.ctx.fillRect(
        this.position.x + 5,
        this.position.y - 8,
        textMetrics.width + 4,
        textHeight
      );
      
      this.ctx.fillStyle = debugColor;
      this.ctx.fillText(
        coordText,
        this.position.x + 7,
        this.position.y + 2
      );
      
      // Show source
      if (this.config.debug.showSource) {
        const sourceText = `src: ${this.debugInfo.positionSource}`;
        if (this.debugInfo.graphemeIndex !== undefined) {
          const fullText = `${sourceText} [${this.debugInfo.graphemeIndex}]`;
          
          const sourceMetrics = this.ctx.measureText(fullText);
          this.ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
          this.ctx.fillRect(
            this.position.x + 5,
            this.position.y + this.position.height + 5,
            sourceMetrics.width + 4,
            textHeight
          );
          
          this.ctx.fillStyle = debugColor;
          this.ctx.fillText(
            fullText,
            this.position.x + 7,
            this.position.y + this.position.height + 15
          );
        }
      }
      
      this.ctx.globalAlpha = 1.0;
    }
  }

  forceVisible(): void {
    this.visible = true;
    this.restartBlinking();
    this.redraw();
  }

  /**
   * Check if caret is currently visible
   */
  isVisible(): boolean {
    return this.isActive && this.visible;
  }

  /**
   * Get current caret position
   */
  getPosition(): CaretPosition {
    return { ...this.position };
  }

  updateConfig(config: Partial<CaretConfig>): void {
    this.config = { ...this.config, ...config };
    if (config.blinkInterval && this.isActive) this.restartBlinking();
    this.redraw();
  }

  /**
   * 🆕 2026-01-20: Enable or disable caret animation
   */
  setAnimationEnabled(enabled: boolean): void {
    if (!this.config.animation) {
      this.config.animation = { enabled, durationMs: 80, easing: 'ease-out' };
    } else {
      this.config.animation.enabled = enabled;
    }
  }

  /**
   * 🆕 2026-01-20: Configure animation settings
   */
  setAnimationConfig(config: { durationMs?: number; easing?: 'linear' | 'ease-out' | 'ease-in-out' }): void {
    if (!this.config.animation) {
      this.config.animation = { enabled: true, durationMs: 80, easing: 'ease-out' };
    }
    if (config.durationMs !== undefined) this.config.animation.durationMs = config.durationMs;
    if (config.easing !== undefined) this.config.animation.easing = config.easing;
  }

  private startBlinking(): void {
    this.stopBlinking();
    this.blinkTimer = window.setInterval(() => {
      this.visible = !this.visible;
      this.redraw(); // only touches overlay, never base
    }, this.config.blinkInterval);
  }

  /**
   * Stop blinking animation
   */
  private stopBlinking(): void {
    if (this.blinkTimer) {
      clearInterval(this.blinkTimer);
      this.blinkTimer = null;
    }
  }

  /**
   * Restart blinking (used when position changes)
   */
  private restartBlinking(): void {
    if (this.isActive) {
      this.stopBlinking();
      this.startBlinking();
    }
  }

  /**
   * Clean up resources
   */
  dispose(): void {
    InputLogger.caret.dispose({ component: 'EdgeDocsCaret' });
    this.hide();
    this.stopBlinking();
    // 🆕 2026-01-20: Cancel any pending animation
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }
  }

  /**
   * Get caret bounds for hit testing
   */
  getBounds(): { x: number; y: number; width: number; height: number } {
    return {
      x: this.position.x - this.config.width / 2,
      y: this.position.y,
      width: this.config.width,
      height: this.position.height
    };
  }
  
  /**
   * 🆕 V8.8: Validate caret position
   * Returns validation result with optional corrected position
   */
  private validatePosition(pos: CaretPosition): {
    valid: boolean;
    issues: string[];
    corrected?: CaretPosition;
  } {
    const issues: string[] = [];
    let corrected: CaretPosition | undefined;
    
    // Check for NaN/Infinity
    if (!isFinite(pos.x) || !isFinite(pos.y) || !isFinite(pos.height)) {
      issues.push('Position contains NaN or Infinity');
      
      if (this.config.validation?.throwOnNaN) {
        throw new Error(`[EdgeDocsCaret] Invalid position: ${JSON.stringify(pos)}`);
      }
      
      corrected = {
        x: isFinite(pos.x) ? pos.x : 0,
        y: isFinite(pos.y) ? pos.y : 0,
        height: isFinite(pos.height) ? pos.height : 16
      };
    }
    
    // Check for negative values
    if (pos.x < 0 || pos.y < 0 || pos.height <= 0) {
      issues.push(`Negative or zero values: x=${pos.x}, y=${pos.y}, h=${pos.height}`);
      
      corrected = corrected || { ...pos };
      if (corrected.x < 0) corrected.x = 0;
      if (corrected.y < 0) corrected.y = 0;
      if (corrected.height <= 0) corrected.height = 16;
    }
    
    // Check canvas bounds (if provided)
    if (this.config.validation?.canvasBounds) {
      const { width, height } = this.config.validation.canvasBounds;
      const outOfBounds = 
        pos.x > width || 
        pos.y > height || 
        pos.y + pos.height > height;
      
      if (outOfBounds) {
        issues.push(`Position outside canvas bounds: (${pos.x}, ${pos.y}) vs canvas (${width}, ${height})`);
        
        if (this.config.validation.clampToCanvas) {
          corrected = corrected || { ...pos };
          corrected.x = Math.min(corrected.x, width - 1);
          corrected.y = Math.min(corrected.y, height - corrected.height);
        }
      }
    }
    
    const valid = issues.length === 0;
    
    if (!valid && this.config.validation?.warnOnInvalid) {
      console.warn('[EdgeDocsCaret] Position validation failed:', {
        original: pos,
        corrected,
        issues
      });
    }
    
    return { valid, issues, corrected };
  }
  
  /**
   * 🆕 V8.8: Set canvas bounds for validation
   */
  setCanvasBounds(width: number, height: number): void {
    if (!this.config.validation) {
      this.config.validation = {
        enabled: false,
        canvasBounds: { width, height }
      };
    } else {
      this.config.validation.canvasBounds = { width, height };
    }
  }
  
  /**
   * 🆕 V8.8: Get diagnostic stats
   */
  getDebugStats(): {
    updateCount: number;
    positionSource: string;
    currentPosition: CaretPosition;
    isVisible: boolean;
    lastUpdateAge: number;
  } | null {
    if (!this.config.debug?.enabled || !this.debugInfo) {
      return null;
    }
    
    return {
      updateCount: this.debugInfo.updateCount,
      positionSource: this.debugInfo.positionSource,
      currentPosition: this.getPosition(),
      isVisible: this.isVisible(),
      lastUpdateAge: Date.now() - this.debugInfo.lastUpdate
    };
  }
}


/**
 * React hook for caret management (optional)
 */
import React from 'react';

export const useEdgeDocsCaret = (
  canvasRef: React.RefObject<HTMLCanvasElement>,
  config?: CaretConfig
) => {
  const [caret, setCaret] = React.useState<EdgeDocsCaret | null>(null);

  React.useEffect(() => {
    if (canvasRef.current && !caret) {
      const newCaret = new EdgeDocsCaret(canvasRef.current, config);
      setCaret(newCaret);

      return () => {
        newCaret.dispose();
      };
    }
  }, [canvasRef, config, caret]);

  return caret;
};
